import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchManagementProfilesV2, updateManagementProfileCustomFields } from '../../../../store/dashboard/overview/overview_apiHelper';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { setExtendedPollingModules, setRefreshReportPreselectedModules, setShowRefreshReport } from '../../../../store/investor/action';
import { getDealFormConfig } from '../../../../store/investor/apiHelpers';
import { useFeatureVisibility } from '../../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../../utils/constants/visibilityKeys';
import {
    TAB_LOCK_IDS_AFTER_DIRECTORS_OVERVIEW,
    TAB_TO_TASK_TRACKER_MAP,
    TabModule,
    VIEW_CONFIGS,
} from '../../../../utils/constants/commonConstants';
import { TAB_LOCK_COMPLETED_STATUSES, TASK_TRACKER_PENDING_STATUSES, TASK_TRACKER_STATUS } from '../RelatedParties/relatedParty_constants';
import { useBoundedPoll } from '../../../../common/hooks/useBoundedPoll';
import { useTaskTrackerResolution } from '../../../../common/hooks/useTaskTrackerResolution';
import { DataTableColumn, DataTableColumnType } from '../../../../common/_custom/DataTable/DataTable.types';
import {
    BASE_DETAILS_INGEST_STEP,
    ColumnLockKey,
    DIN_FETCH_STEP,
    KARZA_DIN_STEP,
    LOCKABLE_COLUMN_FIELD_TO_LOCK_KEY,
    LOCKED_COLUMN_TOOLTIPS,
    LOCK_HEADER_ICON_KEY,
    MANAGEMENT_PROFILE_COMPONENT_FIELDS,
    PROBE_MERGE_WAIT_MS,
} from './ManagementProfileTabConstants';
import {
    clearLinkedInDerivedValues,
    LINKEDIN_ENRICHED_AT_FIELD,
    LINKEDIN_REFRESH_COPY,
    LINKEDIN_ROW_FIELD,
    MANAGEMENT_PROFILE_ROW_TO_UW_FIELD,
    PAN_PROFILE_COPY,
    PAN_PROFILE_ENRICHED_AT_FIELD,
    PAN_ROW_FIELD,
    triggersLinkedInScrape,
} from '../../../../common/ManagementProfile/managementProfileFields';
import { HistoryDrawerState } from '../../../../types/ownerHistory';
import { HISTORY_DRAWER_TITLES } from '../../../../common/HistoryDrawer/constants';
// Not exported from the .d.ts, but every existing consumer (e.g. NumberCellRenderer)
// imports it identically — the file's `export type ActionWithHandlers` makes it a module,
// so `Handlers` resolves as a local type of that module.
import { Handlers } from '../../../../types/actions/types';
import { isPnoUser, toTitleCase } from '../../../../utils/utils';
import { isIndividualPan } from '../../../../utils/validatePAN';

const isTabLockedForColumn = (
    tabId: string,
    tabLockStatus: Record<string, { isLocked?: boolean }> | null,
    taskTrackerMap: Record<string, { status?: string }> | null,
    secondaryModuleList: string[],
): boolean => {
    if (Array.isArray(secondaryModuleList) && !secondaryModuleList.includes(tabId)) {
        const polusEntry = tabLockStatus?.[tabId];
        if (polusEntry == null) return TAB_LOCK_IDS_AFTER_DIRECTORS_OVERVIEW.includes(tabId);
        return !!polusEntry.isLocked;
    }

    const taskTrackerHasEntries = !!taskTrackerMap && Object.keys(taskTrackerMap).length > 0;
    const anyStepCompleteOrFailed =
        taskTrackerHasEntries &&
        (TAB_TO_TASK_TRACKER_MAP[tabId] || []).some((step) => TAB_LOCK_COMPLETED_STATUSES.has(taskTrackerMap?.[step]?.status ?? ''));

    return !anyStepCompleteOrFailed || !!tabLockStatus?.[tabId]?.isLocked;
};

export interface DirectorTableRow {
    // table columns
    name: string;
    din: string;
    imageUrl?: string;
    linkedinUrl?: string;
    followersCount?: string;
    designation?: string;
    shareholdingPercentage?: number | null;
    tenureBeginDate?: number;
    tenureEndDate?: number;
    isActive?: boolean;
    email?: string;
    phone?: string;
    riskIndicatorCount?: number;
    /*
        RECUR-27829. Everything the Regulatory Checks badge reads, under one key.

        confirmed/probable are hit UNIONS rolled up by underwriting-engine from the same per-cell
        rule the Risk Indicators table renders, so the badge shows a number the user can also see
        in the table. They are null (not 0) when UW has not shipped, so the badge can tell "no
        findings" from "no data yet" and fall back to flaggedCategories — the old non-zero
        checksCount-bucket proxy, which is the only thing available in that window.
    */
    amlCount?: {
        confirmed?: number | null;
        probable?: number | null;
        flaggedCategories?: number | null;
    };
    legalCasesCount?: number;
    creditBureau?: string;
    managementProfileSummary?: string;
    // director detail fields (from UW_FETCH_DIRECTOR_OVERVIEW in the aggregated endpoint)
    dob?: string;
    age?: string;
    fatherName?: string;
    gender?: string;
    pan?: string;
    address?: string;
    directorSince?: string;
    directorId?: string;
    creditScore?: number;
    subTitle?: string;
    currentEntities?: any[];
    formerEntities?: any[];
    workExperience?: any[];
    education?: any[];
    // task tracker
    litigationProcessed?: boolean;
    loading: boolean;
    managementProfileId?: string;
    customFields?: Record<string, unknown>;
}

interface ManagementProfileColumnsConfig {
    columns: DataTableColumn[];
    pinnedColumns?: { field: string; position: 'left' | 'right' }[];
}

// Applies (or rolls back) an optimistic custom-field value on a single director row.
// Extracted to module scope to avoid defining it inside the write callback.
// `onlyIfCurrentValueIs` guards the rollback: if the same field was edited again before this
// write settled, the row's stored value is the newer edit, not the value this call optimistically
// applied — rolling back unconditionally would clobber it.
function applyCustomFieldValue(
    setDirectors: Dispatch<SetStateAction<DirectorTableRow[]>>,
    managementProfileId: string,
    customFieldKey: string,
    fieldValue: unknown,
    onlyIfCurrentValueIs?: { value: unknown },
) {
    setDirectors((prev) =>
        prev.map((r) => {
            if (r.managementProfileId !== managementProfileId) return r;
            if (onlyIfCurrentValueIs && r.customFields?.[customFieldKey] !== onlyIfCurrentValueIs.value) return r;
            const nextCustomFields = { ...r.customFields };
            if (fieldValue === undefined) {
                delete nextCustomFields[customFieldKey];
            } else {
                nextCustomFields[customFieldKey] = fieldValue;
            }
            return { ...r, customFields: nextCustomFields };
        }),
    );
}

// Same shape as applyCustomFieldValue, for the editable system fields — those live at the row
// root, not under `customFields`.
function applyRowFieldValue(
    setDirectors: Dispatch<SetStateAction<DirectorTableRow[]>>,
    managementProfileId: string,
    rowField: string,
    fieldValue: unknown,
    onlyIfCurrentValueIs?: { value: unknown },
) {
    setDirectors((prev) =>
        prev.map((r) => {
            if (r.managementProfileId !== managementProfileId) return r;
            if (onlyIfCurrentValueIs && (r as Record<string, any>)[rowField] !== onlyIfCurrentValueIs.value) return r;
            return { ...r, [rowField]: fieldValue };
        }),
    );
}

function useManagementProfileTabLogic() {
    const params = useParams();
    const dispatch = useAppDispatch();

    const [overviewLoading, setOverviewLoading] = useState(true);
    const [awaitingProbeMerge, setAwaitingProbeMerge] = useState(false);
    const [karzaStatusFromFetch, setKarzaStatusFromFetch] = useState<string | undefined>(undefined);
    const [ingestPollExhausted, setIngestPollExhausted] = useState(false);
    const [directors, setDirectors] = useState<DirectorTableRow[]>([]);
    const [totalDirectorsCount, setTotalDirectorsCount] = useState(0);
    const [selectedDirector, setSelectedDirector] = useState<DirectorTableRow | null>(null);
    // undefined = config fetch not yet resolved (table stays in its loading state);
    // null = resolved with no section (or the fetch failed) — columns render empty.
    const [managementProfileColumnsConfig, setManagementProfileColumnsConfig] = useState<ManagementProfileColumnsConfig | null | undefined>(
        undefined,
    );
    const [appliedFilters, setAppliedFilters] = useState<Record<string, any>>({});
    const [historyDrawerState, setHistoryDrawerState] = useState<HistoryDrawerState>({
        open: false,
        referenceId: '',
        fieldKey: '',
        title: '',
    });
    const [linkedinRefresh, setLinkedinRefresh] = useState<{
        managementProfileId: string;
        baselineEnrichedAt: unknown;
        pollKey: string;
    } | null>(null);
    const [panProfileEnrich, setPanProfileEnrich] = useState<{
        managementProfileId: string;
        baselineEnrichedAt: unknown;
        // The PAN that started this enrichment, as typed. The roster's own copy can arrive masked,
        // so it is not a substitute for naming the PAN back to the user.
        pan: string;
        // The roster in hand when the hold was armed, used to tell "not fetched yet" from
        // "fetched and filtered out" — see the completion effect.
        rosterAtArm: DirectorTableRow[];
        pollKey: string;
    } | null>(null);
    // Backs the roster's live region: cleared on arm, filled only on a confirmed rename.
    const [panProfileNameUpdate, setPanProfileNameUpdate] = useState('');
    // The held row's name as last seen before the stamp moved — the enrichment stamps whether or
    // not it matched, so only a name that actually changed is worth announcing.
    const heldRowNameRef = useRef<string | undefined>(undefined);

    const tabLockStatus = useAppSelector((state) => state.investor.tabLockStatus as Record<string, { isLocked?: boolean }> | null);
    const taskTrackerMap = useAppSelector(
        (state) => state.investor.taskTrackerMap as Record<string, { status?: string; dateCreated?: number }> | null,
    );
    const extendedPollingModules = useAppSelector((state) => (state.investor as any).extendedPollingModules ?? []) as string[];
    const secondaryModuleList = useAppSelector(
        (state) => (state.investor.config as any)?.tabs?.filter((t: any) => t.category === 'secondary').map((t: any) => t.id) ?? [],
    );
    const userData = useAppSelector((state) => state.user.userData);
    const platformData = useAppSelector((state) => state.user.domainMetadata);

    const riskAlertsLocked = isTabLockedForColumn(TabModule.RELATED_PARTY, tabLockStatus, taskTrackerMap, secondaryModuleList);
    const regulatoryChecksLocked = isTabLockedForColumn(TabModule.LEGAL_CHECKS, tabLockStatus, taskTrackerMap, secondaryModuleList);
    const legalCasesLocked = isTabLockedForColumn(TabModule.LEGAL_CHECKS, tabLockStatus, taskTrackerMap, secondaryModuleList);

    // Memoized on the individual lock booleans (not on tabLockStatus/taskTrackerMap/
    // secondaryModuleList) so this object keeps a stable reference across renders where the
    // locks didn't actually change — ManagementProfileTab.tsx and the `columns` memo below
    // both key off this reference, and ComponentCellRenderer's React.memo comparator checks
    // componentRenderFnRegistry (built from this) by identity (CodeRabbit #3276).
    const columnLocks: Record<ColumnLockKey, boolean> = useMemo(
        () => ({
            riskAlerts: riskAlertsLocked,
            regulatoryChecks: regulatoryChecksLocked,
            legalCases: legalCasesLocked,
        }),
        [riskAlertsLocked, regulatoryChecksLocked, legalCasesLocked],
    );

    // Filtering is applied by underwriting-engine, so the active filters have to travel with
    // every fetch — including the polling/refresh ones below, which would otherwise reload the
    // full unfiltered list and silently drop the user's filter. They are read from a ref rather
    // than passed in so `loadDirectors` keeps a stable identity: it is a dependency of three
    // effects, and rebuilding it on every filter change would re-fire all of them.
    const appliedFiltersRef = useRef<Record<string, any>>({});

    const loadDirectors = useCallback(
        (orgId: string) =>
            fetchManagementProfilesV2({ orgId, filters: appliedFiltersRef.current })
                .then((res: any) => {
                    const profiles = res?.managementProfile?.profiles || [];
                    const taskTrackerData = res?.taskTrackerData || {};
                    setKarzaStatusFromFetch(taskTrackerData[KARZA_DIN_STEP]);
                    const litigationStep = TAB_TO_TASK_TRACKER_MAP.legalChecks[0];
                    const litigationProcessed =
                        litigationStep in taskTrackerData && taskTrackerData[litigationStep] !== TASK_TRACKER_STATUS.TODO;

                    // @TODO move this sort to BE (active first, then lexicographic by name)
                    const rows: DirectorTableRow[] = [...profiles]
                        .sort((a: any, b: any) => {
                            const aActive = a?.isActive ? 0 : 1;
                            const bActive = b?.isActive ? 0 : 1;
                            if (aActive !== bActive) return aActive - bActive;
                            return (a?.name ?? '').localeCompare(b?.name ?? '', undefined, { sensitivity: 'base' });
                        })
                        .map((p: any) => ({
                            name: p?.name,
                            din: p?.din,
                            imageUrl: p?.customFields?.imageUrl || p?.imageUrl,
                            linkedinUrl: p?.customFields?.linkedinUrl || p?.linkedinUrl,
                            followersCount: p?.customFields?.followersCount ?? p?.followersCount,
                            designation: p?.designation,
                            shareholdingPercentage: p?.shareholdingPercentage,
                            tenureBeginDate: p?.tenureBeginDate,
                            tenureEndDate: p?.tenureEndDate,
                            isActive: p?.isActive,
                            email: p?.email,
                            phone: p?.phone,
                            riskIndicatorCount: p?.riskIndicatorCount,
                            amlCount: p?.amlCount,
                            legalCasesCount: p?.legalCasesCount,
                            creditBureau: p?.creditBureau,
                            managementProfileSummary: p?.managementProfileSummary,
                            dob: p?.dob,
                            age: p?.age,
                            fatherName: p?.fatherName,
                            gender: p?.gender,
                            pan: p?.pan,
                            address: p?.address,
                            directorSince: p?.directorSince,
                            directorId: p?.directorId,
                            creditScore: p?.creditScore,
                            subTitle: p?.subTitle,
                            currentEntities: p?.currentEntities,
                            formerEntities: p?.formerEntities,
                            workExperience: p?.workExperience,
                            education: p?.education,
                            litigationProcessed,
                            loading: false,
                            managementProfileId: p?.managementProfileId,
                            customFields: p?.customFields ?? {},
                        }));
                    setDirectors(rows);
                    // Only an UNFILTERED fetch tells us how many directors the org actually has.
                    // DataTable needs that separately from the row count to tell "this org has
                    // no directors" (totalRowCount 0 → "No data available") apart from "your
                    // filter excluded them all" (totalRowCount > 0, no rows → "No results
                    // found"); with filtering now server-side, rows.length can no longer answer it.
                    if (!Object.keys(appliedFiltersRef.current).length) setTotalDirectorsCount(rows.length);
                })
                .catch(() => {}),
        [],
    );

    useEffect(() => {
        if (!params.id) return;
        setOverviewLoading(true);
        setKarzaStatusFromFetch(undefined);
        setIngestPollExhausted(false);
        loadDirectors(params.id).finally(() => setOverviewLoading(false));
    }, [params.id, loadDirectors]);

    // Org switch: this tab is not remounted when params.id changes (SPA route-param swap),
    // so any per-org UI state left over from the previous org must be reset explicitly here —
    // otherwise a filter/row-selection/drawer scoped to org A silently carries into org B's view.
    useEffect(() => {
        appliedFiltersRef.current = {};
        setAppliedFilters({});
        setTotalDirectorsCount(0);
        setSelectedDirector(null);
        setLinkedinRefresh(null);
        setPanProfileEnrich(null);
        setHistoryDrawerState({ open: false, referenceId: '', fieldKey: '', title: '' });
    }, [params.id]);

    // Config read (D1/GF-9): the `managementProfileColumns` section carries both system and
    // custom columns in one admin-ordered array, and is the ONLY source of columns — there is
    // no FE fallback (removed per PR review). An absent section or a failed fetch resolves to
    // null and the table renders no columns; every PNO is expected to be seeded (backfill
    // script + DEFAULT docs).
    useEffect(() => {
        if (!platformData?.investorOrgId) return;
        getDealFormConfig(
            {
                accountType: userData?.accountType,
                investorOrgId: platformData?.investorOrgId,
                primaryEmail: userData?.primaryEmail,
                configName: VIEW_CONFIGS.AICA_DEAL_NAVBAR_VIEW,
            },
            {
                onSuccess: (configData: any) => setManagementProfileColumnsConfig(configData?.managementProfileColumns ?? null),
                onError: () => setManagementProfileColumnsConfig(null),
            },
        );
    }, [platformData?.investorOrgId, userData?.accountType, userData?.primaryEmail]);

    /*
       The MP response carries the step status inline, so it keeps updating while this page
       self-polls; the redux map only moves while TabSelector polls (capped at 2 min), so it
       is the fallback for the window before the first response lands (and for a response
       that omits the step).
    */
    const reduxKarzaStep = taskTrackerMap?.[KARZA_DIN_STEP];
    const karzaStatus = karzaStatusFromFetch ?? reduxKarzaStep?.status;
    // The dispatch step (AION_FETCH_DIN_DETAILS) can itself be pending before its dependent
    // Karza step even exists in taskTrackerMap — without checking it too, this hook's own
    // "fetching" gate (and the table/banner it drives) would flash the empty state or the
    // fetch banner during that dispatch window even though the shared sidebar predicate
    // (isManagementProfileFetchInFlight, which checks the same DIN_FETCH_STEP) already reads
    // "in flight" and keeps TabSelector's spinner up.
    const dinFetchStepStatus = taskTrackerMap?.[DIN_FETCH_STEP]?.status;

    // Ingest is still running on the BE (dispatched, not yet COMPLETE/FAILED): the tab is
    // unlocked but has no rows yet, so the page loads rather than reporting "no data".
    // Dropped once the poll ceiling is hit so an orphaned step can't spin forever.
    const dinIngestInFlight =
        (TASK_TRACKER_PENDING_STATUSES.includes(karzaStatus ?? '') || TASK_TRACKER_PENDING_STATUSES.includes(dinFetchStepStatus ?? '')) &&
        !ingestPollExhausted;

    // First-run banner gate (D7). Reuses the SAME visibility check that already routes
    // TabSelector's Unlock/Refresh button (handleUnlockRefreshClick) — no new config, no new key.
    const { unlockAicaVisibility } = useFeatureVisibility({
        unlockAicaVisibility: VISIBILITY_KEYS.UNLOCK_AICA.ENABLED,
    });
    // .show is INVERTED: true ⇒ this user is routed to Talk-To-Us, false ⇒ they may self-serve
    // the refresh (see handleUnlockRefreshClick). Negating it is also what makes the gate
    // fail-CLOSED, since getLevel defaults to READ (show:true) when the config is missing or
    // still loading.
    const canSelfServeRefresh = !unlockAicaVisibility.show;
    // AION_INGEST_BASE_DETAILS is owned by the Overview refresh, not Management Profile (it's
    // seeded alongside AION_FETCH_BASE_DETAILS for the overview tab) — an Overview refresh
    // soft-deletes and resets it to TODO_ASYNC (archiveTaskTrackersBeforeRefresh), collaterally,
    // for the whole duration of that refresh. TabSelector already maintains the sticky cache
    // this needs (CROSS_MODULE_TASK_STEPS membership makes a terminal status stick) and publishes
    // it to Redux's crossModuleTaskTrackerCache — reading that here, instead of keeping a second
    // locally-tracked copy, also gets its org-switch reset for free (TabSelector clears it via
    // resetTaskTrackerResolution on every params.id change; this hook is not remounted on that
    // switch, so a local copy would otherwise leak the previous org's terminal status).
    const baseDetailsStatus = taskTrackerMap?.[BASE_DETAILS_INGEST_STEP]?.status;
    const { cache: crossModuleTaskTrackerCache } = useTaskTrackerResolution();
    const cachedBaseDetailsStatus = crossModuleTaskTrackerCache[BASE_DETAILS_INGEST_STEP]?.status;
    // The cached terminal status, once set, wins over a live non-terminal one — that's the same
    // "sticky" precedence TabSelector/RefreshReport apply, and it's what stops a collateral
    // Overview-triggered reset from reading as base-details being back in flight.
    const effectiveBaseDetailsStatus = TAB_LOCK_COMPLETED_STATUSES.has(cachedBaseDetailsStatus ?? '')
        ? cachedBaseDetailsStatus
        : baseDetailsStatus;
    // Banner condition: base-details has reached a TERMINAL status (that's what unlocks the
    // tab — TabSelector's isTerminalStatus, and TAB_LOCK_COMPLETED_STATUSES here, both treat
    // COMPLETE/FAILED/FAILURE as terminal), live or cached, and no DIN step has completed yet.
    // A FAILED base-details ingest therefore still unlocks the tab AND shows the banner, so the
    // user gets a retry prompt instead of landing on a silently empty table. DIN
    // stays COMPLETE-only and uncached: a FAILED DIN step must keep meaning "not ran" so its own
    // retry still shows, and DIN steps are only ever archived by a deliberate MP refresh (whose
    // in-progress treatment is meant to replace the banner), never collaterally by Overview.
    const baseDetailsSettled = TAB_LOCK_COMPLETED_STATUSES.has(effectiveBaseDetailsStatus ?? '');
    // Present (the step exists at all) but not terminal — an org that never ran it has no entry
    // in taskTrackerMap, so effectiveBaseDetailsStatus is undefined and this stays false rather
    // than spinning forever.
    const baseDetailsInFlight = !!effectiveBaseDetailsStatus && !baseDetailsSettled;
    const dinDetailsRan =
        taskTrackerMap?.[DIN_FETCH_STEP]?.status === TASK_TRACKER_STATUS.COMPLETE ||
        taskTrackerMap?.[KARZA_DIN_STEP]?.status === TASK_TRACKER_STATUS.COMPLETE;
    const showFetchBanner = canSelfServeRefresh && baseDetailsSettled && !dinDetailsRan && !dinIngestInFlight;
    // "Fetching" replaces the banner (and the rest of the table) for either module's own
    // in-progress step — reuses the existing overviewLoading/useBoundedPoll treatment below,
    // no new UI.
    const moduleFetchInFlight = dinIngestInFlight || baseDetailsInFlight;

    // CTA opens the existing Unlock/Refresh drawer with Management Profile preselected, rather
    // than firing the enrichment directly — one click used to spend vendor money with no
    // confirmation step. archive-then-start (D6 Trap 1) still happens: RefreshReport's onPrimary
    // calls this same unlockOrRefreshReport with the selected tabs, so routing through the
    // drawer satisfies it exactly as the direct call did.
    const handleFetchDetails = useCallback(() => {
        dispatch(setRefreshReportPreselectedModules([TabModule.MANAGEMENT_PROFILE]));
        dispatch(setShowRefreshReport(true));
    }, [dispatch]);

    // `isPollingCall` mirrors useBoundedPoll's `refetch(true)` convention: a background poll
    // tick stays silent (no loader, so it doesn't flash the table underneath the user), while a
    // user-initiated refetch (e.g. after adding a director) drives the same `overviewLoading`
    // the initial/filter loads use, so the table shows its normal loading state instead of the
    // rows popping in abruptly.
    const refetchDirectors = useCallback(
        (isPollingCall?: boolean) => {
            if (!params.id) return;
            if (!isPollingCall) setOverviewLoading(true);
            loadDirectors(params.id).finally(() => {
                if (!isPollingCall) setOverviewLoading(false);
            });
        },
        [params.id, loadDirectors],
    );

    const handleIngestPollCapExhausted = useCallback(() => setIngestPollExhausted(true), []);

    // MP-local poll so ingest landing after TabSelector's polling window still renders — for
    // either the DIN chain or the base-details auto-populate, both of which land in the same
    // roster. Held back until the mount fetch settles to avoid a duplicate call.
    useBoundedPoll(refetchDirectors, moduleFetchInFlight && !overviewLoading, handleIngestPollCapExhausted);

    const handleLinkedinPollCapExhausted = useCallback(() => {
        setLinkedinRefresh(null);
        toast.info(LINKEDIN_REFRESH_COPY.UNCONFIRMED);
    }, []);

    useBoundedPoll(refetchDirectors, !!linkedinRefresh, handleLinkedinPollCapExhausted, {
        fireImmediately: false,
        stickyAttemptsKey: linkedinRefresh?.pollKey,
    });

    /*
       Completion is inferred from the polled row: the scrape stamps `linkedinEnrichedAt` even
       when it matched nothing, so the stamp moving off its pre-save value means this run landed.
    */
    useEffect(() => {
        if (!linkedinRefresh) return;
        const refreshedRow = directors.find((row) => row.managementProfileId === linkedinRefresh.managementProfileId);
        const enrichedAt = refreshedRow?.customFields?.[LINKEDIN_ENRICHED_AT_FIELD];
        if (!refreshedRow || !enrichedAt || enrichedAt === linkedinRefresh.baselineEnrichedAt) return;

        setLinkedinRefresh(null);
        /*
         The drawer renders from this snapshot, so it has to be re-taken from the fresh row or
         it keeps showing the previous person's headline, experience and photo.
        */
        setSelectedDirector((prev) => (prev?.managementProfileId === refreshedRow.managementProfileId ? refreshedRow : prev));
    }, [directors, linkedinRefresh]);

    const handlePanProfilePollCapExhausted = useCallback(() => {
        setPanProfileEnrich(null);
        toast.info(PAN_PROFILE_COPY.UNCONFIRMED);
    }, []);

    /*
       Adding a director with an individual PAN triggers a server-side PAN Profile enrichment that
       renames the row a few seconds AFTER the create response returns, so the modal's own refetch
       is ordered before the rename can exist and can never observe it. The vendor read has no
       upper bound and a failed one writes nothing at all, so this poll's cap is the only thing
       that ends the wait — `fireImmediately: false` because that modal refetch IS the first look.
    */
    useBoundedPoll(refetchDirectors, !!panProfileEnrich, handlePanProfilePollCapExhausted, {
        fireImmediately: false,
        stickyAttemptsKey: panProfileEnrich?.pollKey,
    });

    // The enrichment stamps `panProfileEnrichedAt` whether or not it matched a name, so the stamp
    // MOVING off its pre-arm baseline means this run landed. A newly created row isn't in the
    // roster yet, so its baseline is undefined and any stamp closes it.
    //
    // The roster is filtered server-side (loadDirectors passes appliedFiltersRef), so an active
    // filter can exclude the held row entirely. Absent is NOT the same as un-enriched: polling on
    // would spend the whole budget and then claim the enrichment could not be confirmed, when in
    // fact it was never observable. Stand down silently instead of crying wolf on a success.
    useEffect(() => {
        if (!panProfileEnrich) return;
        const enrichedRow = directors.find((row) => row.managementProfileId === panProfileEnrich.managementProfileId);
        if (!enrichedRow) {
            // A just-created row is legitimately absent until the next roster arrives, so only a
            // roster that has actually been replaced since arming proves the row is filtered out.
            if (directors !== panProfileEnrich.rosterAtArm) setPanProfileEnrich(null);
            return;
        }
        const enrichedAt = enrichedRow.customFields?.[PAN_PROFILE_ENRICHED_AT_FIELD];
        if (!enrichedAt || enrichedAt === panProfileEnrich.baselineEnrichedAt) {
            heldRowNameRef.current = enrichedRow.name;
            return;
        }
        if (enrichedRow.name && enrichedRow.name !== heldRowNameRef.current) {
            // A row the user had already named is one the PAN RE-named, which is the case worth
            // showing. Without a previous name nothing was overwritten, so it stays sr-only —
            // and the two never both fire, or the rename is announced twice.
            const previousName = heldRowNameRef.current;
            if (previousName) {
                toast.info(PAN_PROFILE_COPY.ENTITY_RENAMED(panProfileEnrich.pan, toTitleCase(enrichedRow.name), toTitleCase(previousName)));
            } else {
                setPanProfileNameUpdate(PAN_PROFILE_COPY.NAME_UPDATED(toTitleCase(enrichedRow.name)));
            }
        }
        // Cleared only AFTER the rename toast: this is what flips the edit modal's
        // `pendingEnrichment` and releases its saved toast, and the container stacks
        // oldest-first, so clearing earlier puts "saved" above the rename.
        setPanProfileEnrich(null);
        // Same snapshot re-take as the LinkedIn effect above, for the same reason.
        setSelectedDirector((prev) => (prev?.managementProfileId === enrichedRow.managementProfileId ? enrichedRow : prev));
    }, [directors, panProfileEnrich]);

    /*
       Armed only for a PAN the backend actually enriches: the two predicates must agree, or the
       poll waits out its whole budget for a request that was never made. The hold is a single
       slot, like the LinkedIn one — a second add REPLACES the first, which stops being tracked —
       and its fresh pollKey buys it a full attempt budget instead of the first's spent one.

       An EDITED row can already carry a stamp — a masked PAN counts as unset so it can be
       re-filled, and another director's enrichment can match this row by name — so the baseline
       captured here, not the stamp's mere presence, is what the completion effect compares against.

       Returns whether it armed. A caller that holds UI open on the enrichment must key off this
       and never re-derive the condition: a create that returns no id arms nothing, and a second
       predicate that disagreed would leave that UI waiting on a hold no poll will ever release.
    */
    const armPanProfileEnrichment = useCallback(
        (managementProfileId?: string, pan?: string | null): boolean => {
            if (!managementProfileId || !isIndividualPan(pan)) return false;
            const armedRow = directors.find((row) => row.managementProfileId === managementProfileId);
            heldRowNameRef.current = armedRow?.name;
            // Empty again for the wait this arms, so the region is silent until this run lands.
            setPanProfileNameUpdate('');
            setPanProfileEnrich({
                managementProfileId,
                baselineEnrichedAt: armedRow?.customFields?.[PAN_PROFILE_ENRICHED_AT_FIELD],
                pan: (pan ?? '').trim().toUpperCase(),
                rosterAtArm: directors,
                pollKey: `${managementProfileId}:${Date.now()}`,
            });
            return true;
        },
        [directors],
    );

    // Cleanup below runs a commit later than the effect that armed the hold, so the module list
    // it filters must be the current one — the effect closure's copy clobbers concurrent additions.
    const extendedPollingModulesRef = useRef(extendedPollingModules);
    useEffect(() => {
        extendedPollingModulesRef.current = extendedPollingModules;
    }, [extendedPollingModules]);

    const previousKarzaStatusRef = useRef<string | undefined>(karzaStatus);
    /*
       TODO(BE): Karza marks AION_KARZA_DIN_DETAILS COMPLETE before Probe is done merging,
       so the loader closes while rows are still mutating. Real fix: emit step-COMPLETE
       from AION's triggerFinalLoad, not KarzaServiceImpl. Until then we just hold the
       loader (MP page + sidebar + drawer) for PROBE_MERGE_WAIT_MS and refetch once.
    */
    useEffect(() => {
        const previous = previousKarzaStatusRef.current;
        previousKarzaStatusRef.current = karzaStatus;

        if (karzaStatus !== TASK_TRACKER_STATUS.COMPLETE) return;
        if (previous === undefined || previous === TASK_TRACKER_STATUS.COMPLETE) return;
        if (!params.id) return;

        setAwaitingProbeMerge(true);
        const mpModule = TabModule.MANAGEMENT_PROFILE;
        if (!extendedPollingModules.includes(mpModule)) {
            dispatch(setExtendedPollingModules([...extendedPollingModules, mpModule]));
        }

        const clearExtendedPolling = () => {
            dispatch(setExtendedPollingModules(extendedPollingModulesRef.current.filter((m) => m !== mpModule)));
        };

        const timer = setTimeout(() => {
            const id = params.id;
            if (!id) {
                setAwaitingProbeMerge(false);
                clearExtendedPolling();
                return;
            }
            loadDirectors(id).finally(() => {
                setAwaitingProbeMerge(false);
                clearExtendedPolling();
            });
        }, PROBE_MERGE_WAIT_MS);

        return () => {
            clearTimeout(timer);
            // The timer is the only other release path, so a re-run that kills it mid-hold must
            // drop the hold here or the loader never closes; a later COMPLETE transition re-arms.
            // Same reasoning covers deps changing (e.g. an org switch) before the deferred refetch
            // fires — otherwise the loader (overviewLoading || awaitingProbeMerge) stays stuck true.
            setAwaitingProbeMerge(false);
            clearExtendedPolling();
        };
    }, [karzaStatus, params.id, loadDirectors, dispatch]);

    // Base columns resolved from the polus config ONLY (no FE fallback), decorated per
    // render with the 3 lockable columns' header padlock — headerIcon/showHeaderIcon/
    // headerIconTooltip (D9). Lock state is runtime (Redux), never config.
    // Memoized on the individual lock BOOLEANS (declared above, ahead of the memoized
    // `columnLocks` object) — DataTable re-derives its own column state whenever the
    // `columns` prop's identity changes, so a new array every render was an infinite render loop.
    const columns = useMemo<DataTableColumn[]>(() => {
        const rawColumns = managementProfileColumnsConfig?.columns ?? [];
        const locks: Record<ColumnLockKey, boolean> = {
            riskAlerts: riskAlertsLocked,
            regulatoryChecks: regulatoryChecksLocked,
            legalCases: legalCasesLocked,
        };

        return rawColumns.map((column) => {
            // The polus seed sends this as a plain TEXT column, which renders through the
            // shared DataTable's default (muted) text color. Route it through the COMPONENT
            // registry instead so its value matches the other data columns' black text.
            if (column.field === MANAGEMENT_PROFILE_COMPONENT_FIELDS.DESIGNATION) {
                return { ...column, type: DataTableColumnType.COMPONENT };
            }
            const lockKey = LOCKABLE_COLUMN_FIELD_TO_LOCK_KEY[column.field];
            if (!lockKey) return column;
            return {
                ...column,
                headerIcon: LOCK_HEADER_ICON_KEY,
                showHeaderIcon: locks[lockKey],
                headerIconTooltip: LOCKED_COLUMN_TOOLTIPS[lockKey],
            };
        });
    }, [managementProfileColumnsConfig, riskAlertsLocked, regulatoryChecksLocked, legalCasesLocked]);

    // Always shown highest-shareholding-first — no header control, just the default row order.
    // Directors with no shareholding % sort last (they have nothing to rank). Client-side only:
    // `directors` already holds every row for the org (GET_ALL_MANAGEMENT_PROFILES is
    // unpaginated), so this is a pure reorder of what's already in memory.
    const sortedDirectors = useMemo<DirectorTableRow[]>(() => {
        return [...directors].sort((rowA, rowB) => {
            const a = rowA.shareholdingPercentage;
            const b = rowB.shareholdingPercentage;
            const aValid = a != null && Number.isFinite(a);
            const bValid = b != null && Number.isFinite(b);
            if (!aValid && !bValid) return 0;
            if (!aValid) return 1;
            if (!bValid) return -1;
            return b - a;
        });
    }, [directors]);

    const pinnedColumns = managementProfileColumnsConfig?.pinnedColumns;

    // Applying a filter refetches rather than narrowing in memory, so the server stays the one
    // authority on which rows the table shows.
    const onFiltersApply = useCallback(
        (filters: Record<string, any>) => {
            appliedFiltersRef.current = filters;
            setAppliedFilters(filters);
            if (params.id) {
                // Same loader the org-load effect drives: server-side filtering means the rows
                // on screen are stale until the refetch lands, so the table must say so.
                setOverviewLoading(true);
                loadDirectors(params.id).finally(() => setOverviewLoading(false));
            }
        },
        [params.id, loadDirectors],
    );

    // ── Write path (D2) — optimistic row update, rolled back on a failed/erroring write ──
    // Handles both custom fields and the five editable system fields; anything else is refused.
    const updateCustomFieldStatus = useCallback(
        (row: DirectorTableRow, option: any, column: any, handlers?: Handlers) => {
            const field: string = column?.field ?? '';
            const assesseeOrgId = params.id;
            const managementProfileId = row?.managementProfileId;
            const customFieldKey = field.startsWith('customFields.') ? field.split('.')[1] : undefined;
            const uwField = MANAGEMENT_PROFILE_ROW_TO_UW_FIELD[field];
            if ((!customFieldKey && !uwField) || !assesseeOrgId || !managementProfileId) return;

            const value = option?.value !== undefined ? option.value : option;
            const previousValue = customFieldKey ? row.customFields?.[customFieldKey] : (row as Record<string, any>)[field];
            const applyValue = (fieldValue: unknown, onlyIfCurrentValueIs?: { value: unknown }) =>
                customFieldKey
                    ? applyCustomFieldValue(setDirectors, managementProfileId, customFieldKey, fieldValue, onlyIfCurrentValueIs)
                    : applyRowFieldValue(setDirectors, managementProfileId, field, fieldValue, onlyIfCurrentValueIs);

            handlers?.onStart?.();
            applyValue(value);

            updateManagementProfileCustomFields({
                assesseeOrgId,
                managementProfileId,
                // A custom field's `field` is already the dotted "customFields.<key>" form the
                // contract's flat `updates` map expects; a system field must travel under the
                // name underwriting-engine stores it as.
                updates: { [uwField ?? field]: value },
            })
                .then((success) => {
                    if (success) {
                        handlers?.onSuccess?.();
                    } else {
                        applyValue(previousValue, { value });
                        handlers?.onError?.();
                    }
                })
                .catch(() => {
                    applyValue(previousValue, { value });
                    handlers?.onError?.();
                });
        },
        [params.id],
    );

    /**
     * The drawer modal's single multi-field save. `updates` is keyed by row field and carries
     * ONLY the fields the user actually changed.
     *
     * On success both the table row and `selectedDirector` are updated from that same map:
     * `selectedDirector` is a snapshot taken once on row click and never re-derived, so without
     * this the open drawer and the row behind it would both keep showing pre-edit values — and
     * reopening the modal would snapshot stale values as its dirty-diff baseline.
     */
    const saveDirectorFields = useCallback(
        async (managementProfileId: string, updates: Record<string, string>): Promise<boolean> => {
            const assesseeOrgId = params.id;
            if (!assesseeOrgId || !managementProfileId || !Object.keys(updates).length) return false;

            const wireUpdates = Object.entries(updates).reduce<Record<string, string>>((acc, [rowField, value]) => {
                const uwField = MANAGEMENT_PROFILE_ROW_TO_UW_FIELD[rowField];
                if (uwField) acc[uwField] = value;
                return acc;
            }, {});
            if (!Object.keys(wireUpdates).length) return false;

            const saved = await updateManagementProfileCustomFields({ assesseeOrgId, managementProfileId, updates: wireUpdates });
            if (!saved) return false;

            // A cleared URL takes the scrape's output with it server-side; strip the same keys from
            // the local copies so the drawer stops showing detail the user just agreed to delete.
            const clearedLinkedIn = LINKEDIN_ROW_FIELD in updates && !updates[LINKEDIN_ROW_FIELD]?.trim();
            const applyEdit = <T extends DirectorTableRow>(row: T): T => {
                const edited = { ...row, ...updates };
                return clearedLinkedIn ? clearLinkedInDerivedValues(edited) : edited;
            };

            setDirectors((prev) => prev.map((r) => (r.managementProfileId === managementProfileId ? applyEdit(r) : r)));
            setSelectedDirector((prev) => (prev?.managementProfileId === managementProfileId ? applyEdit(prev) : prev));

            // `updates` carries only what the user changed, so its keys ARE the "did LinkedIn
            // change" test — no other edited field triggers a re-scrape. A CLEARED URL is
            // excluded: it writes the field but starts no scrape, and the enrichedAt stamp it
            // waited on is unset by that same write, so the poll could only ever time out.
            if (triggersLinkedInScrape(updates)) {
                const savedRow = directors.find((r) => r.managementProfileId === managementProfileId);
                setLinkedinRefresh({
                    managementProfileId,
                    baselineEnrichedAt: savedRow?.customFields?.[LINKEDIN_ENRICHED_AT_FIELD],
                    // Unique per save, and handed to useBoundedPoll as stickyAttemptsKey: polling
                    // never stops between two saves, so without a new key the second scrape
                    // inherits the first's spent attempts and can time out after a single tick.
                    pollKey: `${managementProfileId}:${Date.now()}`,
                });
            }

            // Filling a PAN here dispatches the same server-side enrichment the create path
            // triggers (per-changed-field, ManagementProfileService.updateManagementProfile), and
            // renames the row seconds after this save returns — so the edit path needs the poll too.
            armPanProfileEnrichment(managementProfileId, updates[PAN_ROW_FIELD]);
            return true;
        },
        [params.id, directors, armPanProfileEnrichment],
    );

    const isDirectorEditingEnabled = isPnoUser(userData?.accountType);

    const updateSingleSelectDropdown = useCallback(
        ({ row, option, column, handlers }: { row: any; option: any; column: any; handlers?: Handlers }) => {
            updateCustomFieldStatus(row, option, column, handlers);
        },
        [updateCustomFieldStatus],
    );

    const updateMultiSelectDropdown = useCallback(
        ({ row, selectedValues, column }: { row: any; selectedValues: any; column: any }) => {
            updateCustomFieldStatus(row, selectedValues, column, undefined);
        },
        [updateCustomFieldStatus],
    );

    // ── History (GF-6) — routes Management Profile custom-field history to the new
    // org-scoped UW-backed endpoint via historySource, additive alongside the Leads
    // owner-history path (which never sets assesseeOrgId/historySource, see A-50).
    const onHistoryClick = useCallback(
        (row: DirectorTableRow, columnField: string, columnLabel?: string) => {
            const referenceId = row?.managementProfileId;
            const assesseeOrgId = params.id;
            if (!referenceId || !assesseeOrgId) return;
            const fieldKey = MANAGEMENT_PROFILE_ROW_TO_UW_FIELD[columnField] || columnField?.split('.')?.[1] || columnField || '';
            const title = columnLabel ? `${columnLabel} History` : HISTORY_DRAWER_TITLES.CUSTOM_FIELD_HISTORY;
            setHistoryDrawerState({ open: true, referenceId, fieldKey, title, assesseeOrgId, historySource: 'MANAGEMENT_PROFILE' });
        },
        [params.id],
    );

    const handleHistoryDrawerClose = useCallback(() => {
        setHistoryDrawerState((prev) => ({ ...prev, open: false }));
    }, []);

    return {
        // `=== undefined` holds the loader until the column config resolves — with no FE
        // fallback, rows arriving before the config would otherwise flash a column-less table.
        // Scoped to investorOrgId being present: without it the config effect never fires, and
        // an unresolvable spinner is worse than the column-less table.
        overviewLoading:
            overviewLoading ||
            awaitingProbeMerge ||
            moduleFetchInFlight ||
            (Boolean(platformData?.investorOrgId) && managementProfileColumnsConfig === undefined),
        directors: sortedDirectors,
        totalDirectorsCount,
        columnLocks,
        selectedDirector,
        setSelectedDirector,
        // The date and the status it is claimed for must come from the same row: the redux map is
        // global, so gating the fetch-preferential karzaStatus against it would pair an org-fresh
        // COMPLETE with the previously-viewed org's date.
        lastUpdatedAt: !overviewLoading && reduxKarzaStep?.status === TASK_TRACKER_STATUS.COMPLETE ? reduxKarzaStep.dateCreated : undefined,
        columns,
        pinnedColumns,
        appliedFilters,
        onFiltersApply,
        updateCustomFieldStatus,
        updateSingleSelectDropdown,
        updateMultiSelectDropdown,
        saveDirectorFields,
        isLinkedinRefreshing: !!linkedinRefresh,
        // The Add modal has no selected row, so it reads the hold itself rather than the drawer view.
        isPanProfileEnrichmentPending: !!panProfileEnrich,
        // Row-scoped and live — the drawer's snapshot is taken once on row click, so a hold read
        // off it would stay up for good on the rows the enrichment never lands for.
        isSelectedDirectorPanEnriching:
            !!panProfileEnrich && selectedDirector?.managementProfileId === panProfileEnrich.managementProfileId,
        panProfileNameUpdate,
        armPanProfileEnrichment,
        isDirectorEditingEnabled,
        onHistoryClick,
        historyDrawerState,
        handleHistoryDrawerClose,
        refetchDirectors,
        showFetchBanner,
        handleFetchDetails,
    };
}

export default useManagementProfileTabLogic;
